import React from 'react';
import { Table, Button, Input, Popconfirm, Empty, Tag } from 'antd';
import { PlusOutlined, DeleteOutlined } from '@ant-design/icons';
import type { FP211, FP212 } from '../../../services/p201Api';

const OUTPUT_FORMAT_LABELS: Record<number, string> = {
  1: 'CHANGE/COT/NTL/EOT',
  2: 'DRY/WET',
  3: 'WARP/WEFT',
  4: 'BEFORE WASH/3WASH',
  5: '單一結果',
};

interface Props {
  details: FP211[];
  selectedRow: number;
  onChange: (details: FP211[]) => void;
  disabled: boolean;
}

const Tab3InspectionSpecs: React.FC<Props> = ({ details, selectedRow, onChange, disabled }) => {
  const currentDetail = details[selectedRow];
  if (!currentDetail) {
    return <Empty description="請先在色號明細選取一筆" />;
  }

  const specs = currentDetail.specs || [];

  const addSpec = () => {
    const newSpec: FP212 = {
      fp212_01: currentDetail.fp211_01,
      fp212_02: currentDetail.fp211_02,
      fp212_03: specs.length + 1,
      fp212_11: '',
      fp212_12: '',
      fp212_14: '',
      fp212_90: 0,
      fp212_91: '',
      fp212_92: null,
      fp212_93: '',
    };
    const next = [...details];
    next[selectedRow] = { ...currentDetail, specs: [...specs, newSpec] };
    onChange(next);
  };

  const removeSpec = (specIdx: number) => {
    const next = [...details];
    const nextSpecs = specs.filter((_, i) => i !== specIdx).map((s, i) => ({ ...s, fp212_03: i + 1 }));
    next[selectedRow] = { ...currentDetail, specs: nextSpecs };
    onChange(next);
  };

  const updateSpec = (specIdx: number, field: keyof FP212, value: any) => {
    const next = [...details];
    const nextSpecs = [...specs];
    (nextSpecs[specIdx] as any)[field] = value;
    next[selectedRow] = { ...currentDetail, specs: nextSpecs };
    onChange(next);
  };

  const columns = [
    {
      title: '序號',
      dataIndex: 'fp212_03',
      width: 60,
      onHeaderCell: () => ({ 'data-contract-field': 'fp212_03' } as React.HTMLAttributes<HTMLElement>),
      render: (_: any, __: any, idx: number) => idx + 1,
    },
    {
      title: '測試項目代碼',
      dataIndex: 'fp212_11',
      width: 150,
      onHeaderCell: () => ({ 'data-contract-field': 'fp212_11' } as React.HTMLAttributes<HTMLElement>),
      render: (val: string, _: any, idx: number) => (
        <Input
          value={val}
          disabled={disabled}
          maxLength={6}
          onChange={e => updateSpec(idx, 'fp212_11', e.target.value)}
        />
      ),
    },
    {
      title: '輸出格式',
      key: 'output_format',
      width: 180,
      onHeaderCell: () => ({ 'data-contract-field': 'f11a_15' } as React.HTMLAttributes<HTMLElement>),
      render: (_: any, row: FP212) => {
        if (!row.f11a_15) return null;
        const label = OUTPUT_FORMAT_LABELS[row.f11a_15];
        if (!label) return null;
        return (
          <span>
            {row.f11a_11 && <span style={{ marginRight: 4, color: '#555' }}>{row.f11a_11}</span>}
            <Tag color="blue">{label}</Tag>
          </span>
        );
      },
    },
    {
      title: '檢驗規範代碼',
      dataIndex: 'fp212_12',
      width: 150,
      onHeaderCell: () => ({ 'data-contract-field': 'fp212_12' } as React.HTMLAttributes<HTMLElement>),
      render: (val: string, _: any, idx: number) => (
        <Input
          value={val}
          disabled={disabled}
          maxLength={6}
          onChange={e => updateSpec(idx, 'fp212_12', e.target.value)}
        />
      ),
    },
    {
      title: '公司代號',
      dataIndex: 'fp212_14',
      width: 150,
      onHeaderCell: () => ({ 'data-contract-field': 'fp212_14' } as React.HTMLAttributes<HTMLElement>),
      render: (val: string, _: any, idx: number) => (
        <Input
          value={val}
          disabled={disabled}
          maxLength={12}
          onChange={e => updateSpec(idx, 'fp212_14', e.target.value)}
        />
      ),
    },
    {
      title: '',
      width: 50,
      render: (_: any, __: any, idx: number) =>
        !disabled && (
          <Popconfirm title="確定刪除?" onConfirm={() => removeSpec(idx)}>
            <Button type="text" danger icon={<DeleteOutlined />} size="small" />
          </Popconfirm>
        ),
    },
  ];

  return (
    <div>
      <div style={{ marginBottom: 8, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <span style={{ fontWeight: 500 }}>
          明細第 {selectedRow + 1} 項 - 色號: {currentDetail.fp211_11 || '(未填)'}
        </span>
        {!disabled && (
          <Button type="dashed" icon={<PlusOutlined />} onClick={addSpec}>
            新增檢驗規範
          </Button>
        )}
      </div>
      <Table
        dataSource={specs}
        columns={columns}
        rowKey={(_, idx) => String(idx)}
        pagination={false}
        size="small"
        scroll={{ x: 600 }}
      />
    </div>
  );
};

export default Tab3InspectionSpecs;
